import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RUBRIC_DIMENSIONS,
  PROTECTED_DIMENSION_KEYS,
  MIN_WEIGHT,
  MAX_WEIGHT,
  WEIGHT_STEP,
  WEIGHT_TOTAL,
  MAX_CUSTOM_INSTRUCTION_LENGTH
} from '../../constants/rubricDimensions';
import {
  applyWeightChange,
  changedKeys,
  equalWeights,
  isCustomWeighted,
  previewWeightedTotal,
  sumWeights,
  weightShare,
  type WeightMap
} from '../../lib/rubricWeightBalancer';
import type { RubricCategoryResult } from '../adherence/types';

/**
 * Slide-over editor for one call's rubric dimension weights and its scoring
 * instruction.
 *
 * The ten weights always total WEIGHT_TOTAL, so raising one lowers others:
 * unprotected dimensions give first, and the two Up-Front Contract and two Pain
 * dimensions only give once the rest are exhausted. Any dimension can be pinned
 * so rebalancing skips it.
 *
 * Saving re-scores only the dimensions the edit invalidated — the score preview
 * shown here is exact arithmetic on the existing 1-5 scores, but the re-scored
 * dimensions can come back with different raw scores, so it is labelled as an
 * estimate rather than a promise.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  categories: RubricCategoryResult[];
  initialWeights?: WeightMap;
  initialInstruction?: string;
  initialLockedKeys?: string[];
  /** Tenant-wide default, offered when this call has no instruction yet. */
  defaultInstruction?: string;
  onSave: (payload: {
    weights: WeightMap;
    customInstruction: string;
    lockedKeys: string[];
  }) => Promise<void>;
}

const BAND_THRESHOLDS: Array<{ min: number; label: string; tone: string }> = [
  { min: 0.9, label: 'Send this clip to the team', tone: 'text-teal-700' },
  { min: 0.76, label: 'Strong call', tone: 'text-green-700' },
  { min: 0.6, label: 'Soft call', tone: 'text-amber-700' },
  { min: 0.4, label: 'Deal at real risk', tone: 'text-orange-700' },
  { min: 0, label: 'Deal lost or must reset', tone: 'text-red-700' }
];

/** Mirrors discovery_call_rubric.BANDS so the preview matches what the
 *  analyzer will resolve for the same percentage. */
const resolveBand = (percentage: number) =>
  BAND_THRESHOLDS.find((band) => percentage >= band.min) ?? BAND_THRESHOLDS[BAND_THRESHOLDS.length - 1];

const LockIcon: React.FC<{ locked: boolean }> = ({ locked }) => (
  <span aria-hidden className={locked ? 'text-obsidian' : 'text-gray-300'}>
    {locked ? '\u{1F512}' : '\u{1F513}'}
  </span>
);

/** One dimension row: lock, name, current score, slider, stepper, share. */
const WeightRow: React.FC<{
  label: string;
  weight: number;
  share: number;
  score?: number | null;
  locked: boolean;
  isProtected: boolean;
  hasChanged: boolean;
  onToggleLock: () => void;
  onChange: (next: number) => void;
}> = ({
  label,
  weight,
  share,
  score,
  locked,
  isProtected,
  hasChanged,
  onToggleLock,
  onChange
}) => (
  <div className={`py-2.5 px-3 rounded-lg ${hasChanged ? 'bg-gray-50' : ''}`}>
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onToggleLock}
        className="text-sm leading-none hover:scale-110 transition-transform"
        aria-pressed={locked}
        aria-label={locked ? `Unpin ${label}` : `Pin ${label} so rebalancing skips it`}
        title={locked ? 'Pinned — rebalancing skips this' : 'Pin so rebalancing skips this'}
      >
        <LockIcon locked={locked} />
      </button>

      <span className="text-sm font-medium text-gray-800 flex-1 truncate" title={label}>
        {label}
        {isProtected && (
          <span
            className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
            title="Reduced last when another dimension is raised"
          >
            core
          </span>
        )}
      </span>

      <span className="text-xs font-mono text-gray-500 w-8 text-right">
        {score != null ? `${score}/5` : '—'}
      </span>
    </div>

    <div className="flex items-center gap-3 mt-1.5 pl-6">
      <input
        type="range"
        min={MIN_WEIGHT}
        max={MAX_WEIGHT}
        step={WEIGHT_STEP}
        value={weight}
        disabled={locked}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={`${label} weight`}
        className="flex-1 h-1.5 accent-obsidian disabled:opacity-40 disabled:cursor-not-allowed"
      />

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Number((weight - WEIGHT_STEP).toFixed(1)))}
          disabled={locked || weight <= MIN_WEIGHT}
          className="w-6 h-6 rounded border border-gray-300 text-gray-600 text-sm leading-none hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label={`Decrease ${label} weight`}
        >
          &minus;
        </button>
        <span className="w-11 text-center text-sm font-semibold text-gray-800 tabular-nums">
          {weight.toFixed(1)}&times;
        </span>
        <button
          type="button"
          onClick={() => onChange(Number((weight + WEIGHT_STEP).toFixed(1)))}
          disabled={locked || weight >= MAX_WEIGHT}
          className="w-6 h-6 rounded border border-gray-300 text-gray-600 text-sm leading-none hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label={`Increase ${label} weight`}
        >
          +
        </button>
      </div>

      <span className="w-10 text-right text-xs text-gray-500 tabular-nums">
        {Math.round(share)}%
      </span>
    </div>
  </div>
);

const RubricWeightDrawer: React.FC<Props> = ({
  open,
  onClose,
  categories,
  initialWeights,
  initialInstruction,
  initialLockedKeys,
  defaultInstruction,
  onSave
}) => {
  // What this call is currently graded under — the baseline every diff and the
  // "reset" action compare against.
  const baseline = useMemo<WeightMap>(
    () => ({ ...equalWeights(), ...(initialWeights ?? {}) }),
    [initialWeights]
  );
  const baseInstruction = initialInstruction ?? '';

  const [weights, setWeights] = useState<WeightMap>(baseline);
  const [instruction, setInstruction] = useState(baseInstruction);
  const [lockedKeys, setLockedKeys] = useState<string[]>(initialLockedKeys ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever the drawer reopens so a cancelled edit never leaks into
  // the next one.
  useEffect(() => {
    if (!open) return;
    setWeights(baseline);
    setInstruction(baseInstruction);
    setLockedKeys(initialLockedKeys ?? []);
    setError(null);
  }, [open, baseline, baseInstruction, initialLockedKeys]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !saving && onClose();
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, saving]);

  const scoresByKey = useMemo(() => {
    const map: Record<string, number | null | undefined> = {};
    categories.forEach((cat) => {
      if (cat?.key) map[cat.key] = cat.raw_score;
    });
    return map;
  }, [categories]);

  const handleWeightChange = useCallback(
    (key: string, next: number) => {
      setWeights((current) => applyWeightChange(current, key, next, lockedKeys));
    },
    [lockedKeys]
  );

  const handleToggleLock = useCallback((key: string) => {
    setLockedKeys((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  }, []);

  const dirtyKeys = changedKeys(weights, baseline);
  const instructionChanged = instruction.trim() !== baseInstruction.trim();
  // Mirrors the backend's resolveRescoreKeys: a changed instruction reaches
  // every dimension's prompt, so it invalidates all ten.
  const rescoreCount = instructionChanged ? RUBRIC_DIMENSIONS.length : dirtyKeys.length;
  const hasChanges = dirtyKeys.length > 0 || instructionChanged;

  const current = previewWeightedTotal(scoresByKey, baseline);
  const preview = previewWeightedTotal(scoresByKey, weights);
  const previewBand = resolveBand(preview.percentage);
  const currentBand = resolveBand(current.percentage);
  const bandChanged = previewBand.label !== currentBand.label;

  const total = sumWeights(weights);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ weights, customInstruction: instruction.trim(), lockedKeys });
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Could not save the weights. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Adjust rubric weights">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !saving && onClose()}
        aria-hidden
      />

      <div className="relative w-full max-w-2xl bg-white h-full shadow-3 flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-base font-bold text-obsidian">Adjust rubric weights</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Weights set how much each dimension counts toward this call&rsquo;s score. They do
              not change how strictly any dimension is graded.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none disabled:opacity-40"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Total + reset */}
        <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div className="text-sm">
            <span className="text-gray-500">Total </span>
            <span className="font-bold text-obsidian tabular-nums">
              {total.toFixed(1)} / {WEIGHT_TOTAL.toFixed(1)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setWeights(equalWeights());
              setLockedKeys([]);
            }}
            disabled={!isCustomWeighted(weights)}
            className="text-xs font-medium text-obsidian hover:underline decoration-plasma-cyan decoration-2 underline-offset-2 disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed"
          >
            Reset to equal weights
          </button>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {RUBRIC_DIMENSIONS.map((dimension) => (
            <WeightRow
              key={dimension.key}
              label={dimension.label}
              weight={weights[dimension.key] ?? 0}
              share={weightShare(dimension.key, weights)}
              score={scoresByKey[dimension.key]}
              locked={lockedKeys.includes(dimension.key)}
              isProtected={PROTECTED_DIMENSION_KEYS.includes(dimension.key)}
              hasChanged={dirtyKeys.includes(dimension.key)}
              onToggleLock={() => handleToggleLock(dimension.key)}
              onChange={(next) => handleWeightChange(dimension.key, next)}
            />
          ))}

          {/* Instruction */}
          <div className="px-3 pt-4 pb-2">
            <label
              htmlFor="rubric-custom-instruction"
              className="text-xs font-bold uppercase tracking-wider text-gray-500"
            >
              Scoring instruction for this call
            </label>
            <p className="text-xs text-gray-500 mt-1 mb-2">
              Applies to every dimension. Use it for context the rubric cannot know &mdash;
              e.g. &ldquo;renewal call, the buyer already knows the product.&rdquo; Editing this
              re-scores all {RUBRIC_DIMENSIONS.length} dimensions.
            </p>
            <textarea
              id="rubric-custom-instruction"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value.slice(0, MAX_CUSTOM_INSTRUCTION_LENGTH))}
              rows={4}
              placeholder="Optional context about this specific call…"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-plasma-cyan focus:border-transparent resize-y"
            />
            <div className="flex items-center justify-between mt-1">
              {defaultInstruction && !instruction.trim() ? (
                <button
                  type="button"
                  onClick={() => setInstruction(defaultInstruction)}
                  className="text-xs font-medium text-obsidian hover:underline decoration-plasma-cyan decoration-2 underline-offset-2"
                >
                  Use organization default
                </button>
              ) : (
                <span />
              )}
              <span className="text-[11px] text-gray-400 tabular-nums">
                {instruction.length}/{MAX_CUSTOM_INSTRUCTION_LENGTH}
              </span>
            </div>
          </div>
        </div>

        {/* Footer: preview + actions */}
        <div className="border-t border-gray-200 px-5 py-3 space-y-2.5 bg-white">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Estimated new score
            </span>
            <div className="text-right">
              <div className="text-lg font-bold text-obsidian tabular-nums">
                {preview.total.toFixed(1)}
                <span className="text-sm text-gray-400"> / {preview.max.toFixed(1)}</span>
                <span className="text-sm text-gray-500 ml-1.5">
                  ({Math.round(preview.percentage * 100)}%)
                </span>
              </div>
              <div className={`text-xs font-semibold ${previewBand.tone}`}>
                {previewBand.label}
                {bandChanged && (
                  <span className="text-gray-400 font-normal"> &middot; was {currentBand.label}</span>
                )}
              </div>
            </div>
          </div>

          {hasChanges && (
            <p className="text-xs text-gray-500">
              Based on the current 1&ndash;5 scores.{' '}
              {rescoreCount > 0 && (
                <>
                  {rescoreCount} dimension{rescoreCount === 1 ? '' : 's'} will be re-graded, so the
                  final score may shift.
                </>
              )}
            </p>
          )}

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-0.5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-1.5 rounded text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-4 py-1.5 rounded text-sm font-semibold text-bone bg-obsidian hover:bg-void transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-plasma-cyan focus-visible:ring-offset-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : `Save & re-run${rescoreCount ? ` (${rescoreCount})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RubricWeightDrawer;
